import { useMemo } from "react";

// Phase 3L.5 — vertical-aware fallback label map. Mirrors the canonical
// table in /app/backend/retail/display_labels.py. The backend already
// returns `catalog.order_mode_labels` in the public response, so this
// fallback only fires when an older response or a stale cache hits.
const FALLBACK_LABELS = {
  restaurant: {
    dine_in: "Dine-In", takeout: "Takeout",
    pickup: "Pickup", delivery: "Delivery",
  },
  general_retail: {
    dine_in: "Walk-In", takeout: "Counter Pickup",
    pickup: "Pickup", delivery: "Delivery",
  },
};

// Phase 3M — extracted from RetailCustomer.jsx. Anchors the welcome modal's
// order-type picker + checkout validation. Behaviour identical.
// Phase 3L.5 (Feb 2026) — adds `labelFor()` so call sites render
// vertical-aware labels without rewriting the enabledModeList contract.
export default function useRetailOrderModes(catalog) {
  const orderModes = useMemo(() => catalog?.order_modes || {}, [catalog?.order_modes]);
  // USPS Production Enablement M1 — "shipping" is included here, but the
  // backend (`retail.assistant.build_public_catalog_view`) already ANDs
  // `order_modes.shipping.enabled` against real parcel-shipping readiness
  // (credentialed carrier, origin address, presets, shippable products)
  // before this response is ever sent, so `enabled` being true here means
  // Ship is genuinely usable — not just toggled on by the operator.
  const enabledModeList = useMemo(() => (
    ["dine_in", "takeout", "pickup", "delivery", "shipping"].filter((m) => orderModes?.[m]?.enabled)
  ), [orderModes]);

  const labels = useMemo(() => {
    const serverLabels = catalog?.order_mode_labels;
    if (serverLabels && typeof serverLabels === "object") return serverLabels;
    const vertical = (catalog?.business_vertical || catalog?.vertical || "restaurant").toLowerCase();
    return FALLBACK_LABELS[vertical] || FALLBACK_LABELS.restaurant;
  }, [catalog]);

  const labelFor = (mode) => labels[mode] || mode.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return { orderModes, enabledModeList, labels, labelFor };
}
